import React, { useState, useEffect } from "react";
import "./Reports.css";

export default function Reports() {
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeTasks, setEmployeeTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [config, setConfig] = useState(null);

  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);

  // Load config.json
  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((data) => setConfig(data))
      .catch((err) => console.error("Config fetch error:", err));
  }, []);

  // Fetch employees
  useEffect(() => {
    if (!config) return;
    const fetchEmployees = async () => {
      try {
        const response = await fetch(`${config.BASE_URL}${config.ALL_EMP}`, {
          method: "POST",
          credentials: "include",
          headers: { "X-Requested-With": "RC_EMP_NEW" },
          body: JSON.stringify({}) // fetch all employees
        });
        const data = await response.json();
        setEmployees(data.status === "success" ? data.data || [] : []);
      } catch (error) {
        console.error("Fetch employees failed:", error);
      }
    };
    fetchEmployees();
  }, [config]);

  // Fetch tasks
  useEffect(() => {
    if (!config) return;
    const fetchTasks = async () => {
      try {
        const response = await fetch(`${config.BASE_URL}${config.GET_TASKS}`, {
          method: "GET",
          credentials: "include",
          headers: { "X-Requested-With": "RC_EMP_NEW" },
        });
        const data = await response.json();
        setTasks(data.status === "success" ? data.tasks || [] : []);
      } catch (error) {
        console.error("Fetch tasks failed:", error);
      }
    };
    fetchTasks();
  }, [config]);

  // Filter tasks when employee changes
  useEffect(() => {
    if (selectedEmployee) {
      const filtered = tasks.filter((t) => t.emp_code === selectedEmployee);
      setEmployeeTasks(filtered);
      setSelectedTask(null);
    } else {
      setEmployeeTasks([]);
      setSelectedTask(null);
    }
  }, [selectedEmployee, tasks]);

  // Fetch updates for selected task
  useEffect(() => {
    if (!selectedTask || !config) {
      setUpdates([]);
      return;
    }
    const fetchUpdates = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}${config.GET_TASK_UPDATES}`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "RC_EMP_NEW",
          },
          body: JSON.stringify({
            task_id: selectedTask.task_id,
            emp_code: selectedTask.emp_code,
          }),
        });

        const data = await res.json();
        if (data.status === "success") {
          const formattedUpdates = (data.updates || []).map((u) => {
            const images = [];
            for (let i = 1; i <= 5; i++) {
              if (u[`image${i}`]) images.push(u[`image${i}`]);
            }
            return { ...u, images };
          });
          setUpdates(formattedUpdates);
        } else {
          setUpdates([]);
        }
      } catch (err) {
        console.error("Fetch updates failed:", err);
        setUpdates([]);
      }
    };
    fetchUpdates();
  }, [selectedTask, config]);

  // Lightbox controls
  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };
  const closeLightbox = () => setLightboxIndex(null);
  const showNext = () => {
    if (lightboxImages.length > 1) {
      setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
    }
  };
  const showPrev = () => {
    if (lightboxImages.length > 1) {
      setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    }
  };

  return (
    <>
      <div className="content-card">
        <h2>Reports</h2>

        {/* Employee Dropdown */}
        <div className="dropdown-container">
          <label>Employee:</label>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value="">-- Select Employee --</option>
            {employees.map((emp) => (
              <option key={emp.emp_code} value={emp.emp_code}>
                {emp.emp_code} - {emp.name} ({emp.role} - {emp.department})
              </option>
            ))}
          </select>
        </div>

        {/* Task Dropdown */}
        <div className="dropdown-container">
          <label>Tasks:</label>
          <select
            disabled={!selectedEmployee}
            value={selectedTask ? String(selectedTask.task_id) : ""}
            onChange={(e) => {
              const task = employeeTasks.find(
                (t) => String(t.task_id) === e.target.value
              );
              setSelectedTask(task || null);
            }}
          >
            <option value="">-- Select Task --</option>
            {employeeTasks.map((task) => (
              <option key={task.task_id} value={String(task.task_id)}>
                {task.job_title} (#{task.task_id})
              </option>
            ))}
          </select>
        </div>

        {/* Task Details */}
        {selectedTask && (
          <div className="task-details-card">
            <h3 className="task-title">Task Details</h3>
            <div className="task-row">
              <div><strong>Task ID:</strong> {selectedTask.task_id}</div>
              <div><strong>Title:</strong> {selectedTask.job_title}</div>
              <div>
                <strong>Status:</strong>{" "}
                <span style={{ color: selectedTask.task_status === 1 ? "green" : "red", fontWeight: "bold" }}>
                  {selectedTask.task_status === 1 ? "Completed" : "Pending"}
                </span>
              </div>
            </div>
            <div className="task-row single">
              <div>
                <strong>Employee:</strong>{" "}
                {selectedTask.emp_code} - {selectedTask.employee_name} ({selectedTask.role}, {selectedTask.dept})
              </div>
            </div>
            <div className="task-row">
              <div><strong>Start Date:</strong> {selectedTask.start_date}</div>
              <div><strong>End Date:</strong> {selectedTask.target_date}</div>
              <div>
                <strong>Count of Days:</strong>{" "}
                {Math.ceil(
                  (new Date(selectedTask.target_date) - new Date(selectedTask.start_date)) /
                  (1000 * 60 * 60 * 24)
                )}
              </div>
            </div>
            <div className="task-row single">
              <div><strong>Description:</strong> {selectedTask.job_description}</div>
            </div>
            <div className="task-row single">
              <strong>Images:</strong>
              <div className="task-images">
                {([1, 2, 3, 4, 5].map(i => selectedTask[`image${i}`]).filter(Boolean)).length > 0 ? (
                  ([1, 2, 3, 4, 5].map(i => selectedTask[`image${i}`]).filter(Boolean)).map((imgData, idx) => (
                    <img
                      key={idx}
                      src={`data:image/jpeg;base64,${imgData}`}
                      alt={`Task Image ${idx + 1}`}
                      onClick={() => openLightbox(
                        [1, 2, 3, 4, 5].map(i => selectedTask[`image${i}`]).filter(Boolean),
                        idx
                      )}
                    />
                  ))
                ) : (
                  <p className="no-image">No images available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Task Updates Table */}
        {selectedTask && (
          <div className="updates-section">
            <h3>Task Updates</h3>
            <table className="updates-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Remark</th>
                  <th>Images</th>
                </tr>
              </thead>
              <tbody>
                {updates.length > 0 ? (
                  updates.map((u, idx) => (
                    <tr key={u.update_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{u.update_date}</td>
                      <td>{u.remark}</td>
                      <td>
                        {u.images.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => openLightbox(u.images, 0)}
                          >
                            View Images
                          </button>
                        ) : (
                          "No Images"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No updates available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>
            <button
              className="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                showPrev();
              }}
            >
              ‹
            </button>
            <img
              src={`data:image/jpeg;base64,${lightboxImages[lightboxIndex]}`}
              alt={`Image ${lightboxIndex + 1}`}
            />
            <button
              className="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                showNext();
              }}
            >
              ›
            </button>
            <p>
              Image {lightboxIndex + 1} of {lightboxImages.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
